import type { EtiquetaPlantilla } from '../api';
import { anotarRollo, cuantasEntran, descontar, esDelRollo, guardarPendiente } from './rollo';

/**
 * Manda etiquetas a la impresora.
 *
 * Se abre una ventana con los dibujos ya hechos y la medida del papel puesta
 * en `@page`: sin eso el navegador imprime en A4 y la etiqueta sale escalada,
 * que en una térmica significa tirar el rollo. Una etiqueta por hoja.
 *
 * Va por el navegador a propósito. La impresora se instala en Windows como
 * cualquier otra y esto anda sin pedirle nada a nadie; el día que haga falta
 * hablarle en TSPL directamente, se agrega al lado sin tocar el diseñador.
 */
type Medida = Pick<EtiquetaPlantilla, 'nombre' | 'ancho' | 'alto'>;

/**
 * La hoja que se manda a imprimir, con una etiqueta por página.
 *
 * El dibujo llega copiado de la pantalla (`innerHTML`), y en la pantalla la
 * vista previa se achica para que entren varias al lado: el SVG viene con un
 * alto en píxeles puesto a mano. Copiado tal cual, eso es lo que se imprime, y
 * la etiqueta sale de un centímetro en una esquina del rollo. Por eso acá se
 * le vuelve a fijar la medida del papel, y con `!important`, que es lo único
 * que le gana a un estilo escrito en el propio elemento.
 */
export function paginaDeImpresion(etiqueta: Medida, dibujos: string[]): string {
  return (
    `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${etiqueta.nombre}</title>` +
    `<style>@page{size:${etiqueta.ancho}mm ${etiqueta.alto}mm;margin:0}` +
    `html,body{margin:0;padding:0}` +
    `.hoja{page-break-after:always;break-after:page}` +
    `.hoja:last-child{page-break-after:auto;break-after:auto}` +
    `svg{display:block;width:${etiqueta.ancho}mm!important;height:${etiqueta.alto}mm!important}` +
    `</style></head><body>` +
    dibujos.map((d) => `<div class="hoja">${d}</div>`).join('') +
    `</body></html>`
  );
}

/**
 * Devuelve el problema para mostrar, o null si salió todo. Una tirada del
 * rollo que no entra en lo que le queda imprime las que entran y deja el resto
 * pendiente (`lib/rollo.ts`): el texto que vuelve lo dice.
 */
export function imprimirEtiquetas(etiqueta: Medida, todos: string[]): string | null {
  if (todos.length === 0) return 'No hay nada para imprimir.';

  const delRollo = esDelRollo(etiqueta);
  const entran = delRollo ? cuantasEntran(todos.length) : todos.length;
  const resto = todos.slice(entran);
  if (entran === 0) {
    guardarPendiente({ nombre: etiqueta.nombre, dibujos: resto });
    return `El rollo no tiene más etiquetas: las ${resto.length} quedan pendientes. Cambiá el rollo y imprimilas desde «Imprimir etiquetas».`;
  }
  const dibujos = todos.slice(0, entran);

  const ventana = window.open('', '_blank', 'width=520,height=680');
  if (!ventana) return 'El navegador bloqueó la ventana de impresión. Permitila y probá de nuevo.';

  ventana.document.write(paginaDeImpresion(etiqueta, dibujos));
  ventana.document.close();
  ventana.focus();

  // Lo que sale del rollo de la térmica se descuenta de su contador. La API se
  // importa recién acá para que las pruebas puedan usar este archivo sin
  // levantarla; y si el aviso falla, la etiqueta igual salió.
  if (delRollo) {
    descontar(dibujos.length);
    void import('../api')
      .then(({ api }) => api.usarRollo(dibujos.length))
      .then((r) => anotarRollo(r.rolloEtiquetas))
      .catch(() => {});
  }

  // Se le da un respiro al navegador para que dibuje antes de abrir el
  // diálogo: si se imprime demasiado pronto, salen hojas en blanco.
  setTimeout(() => {
    ventana.print();
    ventana.close();
  }, 400);

  if (resto.length === 0) return null;
  guardarPendiente({ nombre: etiqueta.nombre, dibujos: resto });
  return `El rollo alcanzaba para ${entran}: salen esas y las otras ${resto.length} quedan pendientes. Cambiá el rollo y imprimilas desde «Imprimir etiquetas».`;
}

/**
 * Imprime una tirada, pidiendo confirmación antes si pasa de `umbral`: un
 * rollo grande tarda un buen rato en salir y no se puede deshacer. Devuelve el
 * problema para mostrar (vacío si salió bien), o null si se cancelaron.
 * `confirmar` es el de `useConfirmar`.
 */
export async function imprimirTirada(
  confirmar: (p: { titulo: string; detalle: string; boton: string }) => Promise<boolean>,
  etiqueta: Medida,
  dibujos: string[],
  umbral: number,
): Promise<string | null> {
  const n = dibujos.length;
  if (n > umbral) {
    const ok = await confirmar({
      titulo: `¿Imprimir ${n} etiquetas?`,
      detalle: 'Es una tirada grande: el rollo va a tardar un buen rato en salir.',
      boton: `Imprimir ${n} etiquetas`,
    });
    if (!ok) return null;
  }
  return imprimirEtiquetas(etiqueta, dibujos) ?? '';
}

/**
 * Manda a la impresora hojas de papel común: el remito y lo que venga después.
 *
 * Es hermana de `imprimirEtiquetas` y no la misma función porque la de
 * etiquetas le clava a cada SVG la medida del rollo con `!important` —lo que
 * necesita la térmica— y eso sobre un documento A4 haría que cualquier ícono
 * ocupe la hoja entera. Comparten la idea (ventana nueva, `@page`, una hoja
 * por página) y no el CSS.
 *
 * El HTML llega copiado de la pantalla, donde el documento se dibuja con
 * estilos en el propio elemento: por eso no hace falta traerse ninguna hoja de
 * estilos, lo único que se agrega acá es lo que no se puede escribir inline.
 */
export function paginaDeDocumento(titulo: string, hojas: string[], anchoMm = 210, altoMm = 297): string {
  return (
    `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${titulo}</title>` +
    `<style>@page{size:${anchoMm}mm ${altoMm}mm;margin:0}` +
    `html,body{margin:0;padding:0;background:#fff}` +
    `.hoja{width:${anchoMm}mm;height:${altoMm}mm;overflow:hidden;page-break-after:always;break-after:page}` +
    `.hoja:last-child{page-break-after:auto;break-after:auto}` +
    // Sin esto el navegador descarta los fondos —la barra gris del encabezado
    // de la tabla— y el remito sale distinto del que se vio en pantalla.
    `*{-webkit-print-color-adjust:exact;print-color-adjust:exact}` +
    `</style></head><body>` +
    hojas.map((h) => `<div class="hoja">${h}</div>`).join('') +
    // Se espera a que carguen las imágenes antes de abrir el diálogo. El logo
    // es un archivo aparte: imprimir a los 400 ms y cruzar los dedos es cómo
    // sale un remito sin logo, que en un papel que se entrega no es un detalle.
    `<script>(function(){var ims=[].slice.call(document.images);` +
    `Promise.all(ims.map(function(i){return i.complete?null:new Promise(function(r){i.onload=i.onerror=r;});}))` +
    `.then(function(){setTimeout(function(){window.focus();window.print();window.close();},80);});})();</scr` +
    `ipt></body></html>`
  );
}

export function imprimirDocumento(titulo: string, hojas: string[]): string | null {
  if (hojas.length === 0) return 'No hay nada para imprimir.';

  const ventana = window.open('', '_blank', 'width=900,height=980');
  if (!ventana) return 'El navegador bloqueó la ventana de impresión. Permitila y probá de nuevo.';

  ventana.document.write(paginaDeDocumento(titulo, hojas));
  ventana.document.close();
  ventana.focus();
  return null;
}
